import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/hooks/useAuth";
import { usePreferences } from "@/hooks/usePreferences";
import { client } from "@/services/amplifyClient";
import { signInWithRedirect, updateUserAttributes } from "aws-amplify/auth";
import { Bell, ExternalLink, FileText, Loader2, LogOut, MapPin, Pencil, RefreshCw, Save, Settings, Shield, User, Wrench, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "wouter";

export default function Profile() {
  const { user, isAuthenticated, isLoading: authLoading, logout, mockLogin, refreshUser } = useAuth();
  const { preferences, updatePreferences, initializePreferences, isLoading: prefsLoading } = usePreferences();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isCalculatingTop10, setIsCalculatingTop10] = useState(false);

  // Edit mode state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [editName, setEditName] = useState("");

  // Initialize preferences for new users
  useEffect(() => {
    if (isAuthenticated && !prefsLoading && !preferences) {
      initializePreferences();
    }
  }, [isAuthenticated, prefsLoading, preferences, initializePreferences]);

  // Derive values from preferences with defaults
  const temperatureUnit = preferences?.temperatureUnit === 'CELSIUS' ? 'celsius' : 'fahrenheit';
  const windSpeedUnit = preferences?.windSpeedUnit === 'KMH' ? 'kmh' : preferences?.windSpeedUnit === 'MS' ? 'ms' : 'mph';
  const timeFormat = preferences?.timeFormat === 'TWENTY_FOUR_HOUR' ? '24h' : '12h';

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      // Logout error (console removed)
    }
    setIsLoggingOut(false);
  };

  const handleTemperatureChange = (unit: "celsius" | "fahrenheit") => {
    const apiValue = unit === 'celsius' ? 'CELSIUS' : 'FAHRENHEIT';
    updatePreferences({ temperatureUnit: apiValue });
    toast.success(`Temperature unit changed to ${unit === 'celsius' ? 'Celsius' : 'Fahrenheit'}`);
  };

  const handleWindSpeedChange = (unit: "mph" | "kmh" | "ms") => {
    const apiValue = unit === 'kmh' ? 'KMH' : unit === 'ms' ? 'MS' : 'MPH';
    updatePreferences({ windSpeedUnit: apiValue });
    toast.success(`Wind speed unit changed to ${unit}`);
  };

  const handleTimeFormatChange = (format: "12h" | "24h") => {
    const apiValue = format === '24h' ? 'TWENTY_FOUR_HOUR' : 'TWELVE_HOUR';
    updatePreferences({ timeFormat: apiValue });
    toast.success(`Time format changed to ${format === '24h' ? '24-hour' : '12-hour'}`);
  };

  const handleStartEditing = () => {
    setEditName(user?.name || "");
    setIsEditingProfile(true);
  };

  const handleCancelEditing = () => {
    setIsEditingProfile(false);
    setEditName("");
  };

  const handleSaveProfile = async () => {
    if (!user?.id) return;

    const trimmedName = editName.trim();
    if (!trimmedName) {
      toast.error("Name cannot be empty");
      return;
    }

    setIsSavingProfile(true);

    try {
      // Check if this is a mock user (demo mode)
      const isMockUser = user.id === 'mock-user-123';

      if (!isMockUser) {
        // Update Cognito user attributes (name)
        await updateUserAttributes({
          userAttributes: {
            name: trimmedName,
          },
        });
      }

      // Update User record in DynamoDB
      const { errors } = await client.models.User.update({
        id: user.id,
        name: trimmedName,
      });

      if (errors && errors.length > 0) {
        throw new Error(errors[0].message);
      }

      // Refresh user data to get updated info
      await refreshUser();

      setIsEditingProfile(false);
      setEditName("");
      toast.success("Profile updated successfully");
    } catch (error) {
      // Error updating profile (console removed)
      toast.error("Failed to update profile. Please try again.");
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleTriggerTop10Calculation = async () => {
    setIsCalculatingTop10(true);
    try {
      const response = await client.queries.triggerTop10Calculation({});
      if (response.data?.success) {
        toast.success(`Top 10 calculated! ${response.data.locationsProcessed} locations processed in ${response.data.executionTimeMs}ms`);
      } else {
        toast.error(response.data?.message || "Failed to calculate Top 10");
      }
    } catch (error) {
      // Top10 calculation error (console removed)
      toast.error("Failed to trigger Top 10 calculation");
    } finally {
      setIsCalculatingTop10(false);
    }
  };

  if (authLoading || (isAuthenticated && prefsLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md w-full mx-4 card-elevated border-none shadow-premium">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <User className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Sign In Required</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-6">
            <p className="text-muted-foreground text-lg">
              Sign in to view and manage your profile
            </p>
            <div className="space-y-3">
              <Button
                size="lg"
                onClick={() => signInWithRedirect()}
                className="w-full"
              >
                Sign In with AWS Cognito
              </Button>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">Or</span>
                </div>
              </div>
              <Button
                size="lg"
                variant="outline"
                onClick={() => mockLogin()}
                className="w-full"
              >
                Continue as Demo User
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const initials = user.name
    ? user.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : user.email?.[0]?.toUpperCase() || "U";

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Header */}
      <section className="relative py-12 sm:py-16 md:py-24 lg:py-32 bg-gradient-to-br from-primary/10 via-background to-background">
        <div className="container text-center">
          <div className="max-w-3xl mx-auto space-y-4 sm:space-y-6">
            <Avatar className="w-20 h-20 sm:w-24 sm:h-24 mx-auto shadow-xl border-4 border-background">
              <AvatarFallback className="bg-gradient-to-r from-amber-500 to-orange-600 text-white text-2xl sm:text-3xl font-bold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-2">{user.name || "User"}</h1>
              <p className="text-lg sm:text-xl text-muted-foreground">{user.email}</p>
            </div>
            <div className="flex items-center justify-center gap-3">
              <Badge variant="secondary" className="text-sm px-3 sm:px-4 py-2">
                <Shield className="h-4 w-4 mr-2" />
                Member
              </Badge>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container py-8 sm:py-10 md:py-12">
        <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
          {/* Account Information */}
          <Card className="card-elevated border-none shadow-premium">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Account Information
                  </CardTitle>
                  <CardDescription>Your personal details and account status</CardDescription>
                </div>
                {!isEditingProfile && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleStartEditing}
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {isEditingProfile ? (
                // Edit Mode
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-name">Full Name</Label>
                    <Input
                      id="edit-name"
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      placeholder="Enter your name"
                      disabled={isSavingProfile}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email Address</Label>
                    <p className="text-sm text-muted-foreground">
                      {user.email || "Not provided"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Email cannot be changed here. Contact support if you need to update your email.
                    </p>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button
                      onClick={handleSaveProfile}
                      disabled={isSavingProfile}
                    >
                      {isSavingProfile ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Save Changes
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleCancelEditing}
                      disabled={isSavingProfile}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div className="grid gap-3 sm:gap-4 grid-cols-1 md:grid-cols-2">
                  <div className="space-y-1 sm:space-y-2">
                    <label className="text-xs sm:text-sm font-medium text-muted-foreground">Full Name</label>
                    <p className="text-sm sm:text-base font-medium">{user.name || "Not provided"}</p>
                  </div>
                  <div className="space-y-1 sm:space-y-2">
                    <label className="text-xs sm:text-sm font-medium text-muted-foreground">Email Address</label>
                    <p className="text-sm sm:text-base font-medium break-all">{user.email || "Not provided"}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="card-elevated border-none shadow-premium">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Quick Actions
              </CardTitle>
              <CardDescription>Manage your preferences and settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link href="/locations">
                <Button variant="outline" className="w-full justify-start" size="lg">
                  <MapPin className="h-5 w-5 mr-3" />
                  Manage Saved Locations
                </Button>
              </Link>
              <Link href="/notifications">
                <Button variant="outline" className="w-full justify-start" size="lg">
                  <Bell className="h-5 w-5 mr-3" />
                  Notification Settings
                </Button>
              </Link>
              <Separator />
              <Button
                variant="destructive"
                className="w-full justify-start"
                size="lg"
                onClick={handleLogout}
                disabled={isLoggingOut}
              >
                {isLoggingOut ? (
                  <Loader2 className="h-5 w-5 mr-3 animate-spin" />
                ) : (
                  <LogOut className="h-5 w-5 mr-3" />
                )}
                Sign Out
              </Button>
            </CardContent>
          </Card>

          {/* App Preferences */}
          <Card className="card-elevated border-none shadow-premium">
            <CardHeader>
              <CardTitle>App Preferences</CardTitle>
              <CardDescription>Customize your SunSquatcher experience</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label id="temp-unit-label" className="text-sm font-medium">Temperature Units</label>
                <div className="flex gap-2" role="group" aria-labelledby="temp-unit-label">
                  <Button
                    variant={temperatureUnit === "celsius" ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleTemperatureChange("celsius")}
                    aria-pressed={temperatureUnit === "celsius"}
                  >
                    Celsius (°C)
                  </Button>
                  <Button
                    variant={temperatureUnit === "fahrenheit" ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleTemperatureChange("fahrenheit")}
                    aria-pressed={temperatureUnit === "fahrenheit"}
                  >
                    Fahrenheit (°F)
                  </Button>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <label id="wind-unit-label" className="text-sm font-medium">Wind Speed Units</label>
                <div className="flex gap-2" role="group" aria-labelledby="wind-unit-label">
                  <Button
                    variant={windSpeedUnit === "kmh" ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleWindSpeedChange("kmh")}
                    aria-pressed={windSpeedUnit === "kmh"}
                  >
                    km/h
                  </Button>
                  <Button
                    variant={windSpeedUnit === "mph" ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleWindSpeedChange("mph")}
                    aria-pressed={windSpeedUnit === "mph"}
                  >
                    mph
                  </Button>
                  <Button
                    variant={windSpeedUnit === "ms" ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleWindSpeedChange("ms")}
                    aria-pressed={windSpeedUnit === "ms"}
                  >
                    m/s
                  </Button>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <label id="time-format-label" className="text-sm font-medium">Time Format</label>
                <div className="flex gap-2" role="group" aria-labelledby="time-format-label">
                  <Button
                    variant={timeFormat === "24h" ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleTimeFormatChange("24h")}
                    aria-pressed={timeFormat === "24h"}
                  >
                    24-hour
                  </Button>
                  <Button
                    variant={timeFormat === "12h" ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleTimeFormatChange("12h")}
                    aria-pressed={timeFormat === "12h"}
                  >
                    12-hour (AM/PM)
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Privacy & Security */}
          <Card className="card-elevated border-none shadow-premium">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Privacy & Security
              </CardTitle>
              <CardDescription>Your data and account security</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
                <Shield className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium">Your account is secure</p>
                  <p className="text-sm text-muted-foreground">
                    Authenticated via AWS Cognito with industry-standard encryption
                  </p>
                </div>
              </div>
              <Separator />
              <div className="space-y-2">
                <p className="text-sm font-medium">Data Collection</p>
                <p className="text-sm text-muted-foreground">
                  We only collect location data you explicitly save and weather forecast information
                  necessary to provide predictions. Your data is never shared with third parties.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Legal */}
          <Card className="card-elevated border-none shadow-premium">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Legal
              </CardTitle>
              <CardDescription>Privacy policy and terms of service</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link href="/privacy">
                <Button variant="outline" className="w-full justify-between" size="lg">
                  <span className="flex items-center">
                    <Shield className="h-5 w-5 mr-3" />
                    Privacy Policy
                  </span>
                  <ExternalLink className="h-4 w-4 text-muted-foreground" />
                </Button>
              </Link>
              <Link href="/terms">
                <Button variant="outline" className="w-full justify-between" size="lg">
                  <span className="flex items-center">
                    <FileText className="h-5 w-5 mr-3" />
                    Terms of Service
                  </span>
                  <ExternalLink className="h-4 w-4 text-muted-foreground" />
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Developer Tools - for sandbox testing */}
          <Card className="card-elevated border-none shadow-premium border-dashed border-amber-500/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="h-5 w-5 text-amber-500" />
                Developer Tools
              </CardTitle>
              <CardDescription>Testing and debugging utilities (sandbox only)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <p className="text-sm font-medium">Manual Top 10 Calculation</p>
                <p className="text-sm text-muted-foreground mb-3">
                  Trigger the Top 10 sunrise/sunset calculation job manually. This normally runs automatically at midnight UTC.
                </p>
                <Button
                  onClick={handleTriggerTop10Calculation}
                  disabled={isCalculatingTop10}
                  variant="outline"
                  className="w-full sm:w-auto"
                >
                  {isCalculatingTop10 ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Calculate Top 10 Now
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
